import { useTheme } from '@mui/material/styles';
import { AUTH, ANALYTICS } from '../../../config/app-config';
import { getResponseErrorMessage, getRequestErrorMessage } from '../../../utils/apiErrors';
import useDashboardNotifier from './useDashboardNotifier';
import React, { useState, useEffect } from 'react';
import {
    Typography,
    Box,
    Button,
    TextField,
    Tabs,
    Tab,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    CircularProgress,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { secureFetch } from "../../../utils/secureFetch";

const Analytics = ({ open, onClose, adminApiBase }) => {
    const theme = useTheme();
    const { notify, notification } = useDashboardNotifier();
    // ---------------------------------------------------------------- API-State
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // -------------------------------------------------------------- Dummy-Suche
    const dummySearches = [
        { id: 1, phrase: 'test 1', count: 12, date: '2025-07-01T09:00:00Z' },
        { id: 2, phrase: 'test 2', count: 7, date: '2025-07-02T15:30:00Z' },
        { id: 3, phrase: 'test 3', count: 4, date: '2025-06-28T11:45:00Z' },
    ];

    // -------------------------------------------------------------- Datumsfilter
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);

    const [startDate, setStartDate] = useState(weekAgo.toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));

    // -------------------------------------------------------------- Sessions laden
    useEffect(() => {
        const ctrl = new AbortController();

        const fetchSessions = async () => {
            setLoading(true);
            try {
                const url =
                    `${adminApiBase}admin/article-views` +
                    `?start=${startDate}&end=${endDate}&limit=500`;

                const res = await secureFetch(url, {
                    method: 'GET',
                    signal: ctrl.signal,
                }, true);

                if (!res.ok) throw new Error(await getResponseErrorMessage(res, 'Failed to load analytics.'));
                const data = await res.json();

                const normalized = data
                    .map((u) => ({
                        user: u.user_id,
                        sessions: u.views.map((v) => ({
                            id: v.id,
                            started: v.timestamp,
                            articles: [v.title], // ggf. erweitern
                        })),
                    }))
                    .sort((a, b) => a.user.localeCompare(b.user));

                setSessions(normalized);
                setError(null);
            } catch (e) {
                if (!ctrl.signal.aborted && e.name !== 'AbortError') {
                    const message = getRequestErrorMessage(e, 'Failed to load analytics.');
                    setError(message);
                    notify(message, 'error');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSessions();
        return () => ctrl.abort();
    }, [adminApiBase, startDate, endDate, notify]);

    // -------------------------------------------------------------- UI-State
    const [tab, setTab] = useState('sessions');
    const [openUser, setOpenUser] = useState(null);

    // -------------------------------------------------------------- Render
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Analytics
                </Typography>

                <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                    <TextField
                        label="Start"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                    <TextField
                        label="End"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                    {ANALYTICS.dashboardUrl && (
                        <Button
                            variant="contained"
                            href={ANALYTICS.dashboardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<LaunchIcon fontSize="small" />}
                            sx={{ whiteSpace: 'nowrap', bgcolor: theme.palette.primary.main, color: 'white' }}
                        >
                            {ANALYTICS.label || 'Analytics'}
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Tabs */}
            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ mb: 3 }}
            >
                <Tab value="sessions" label="User Sessions" />
                <Tab value="searches" label="Searched but not found" />

            </Tabs>

            {/* ------------- Tab: Searches (Dummy) */}
            {tab === 'searches' && (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: '#fff' }}>Phrase</TableCell>
                            <TableCell sx={{ color: '#fff' }}>Searches</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dummySearches.map((s) => (
                            <TableRow key={s.id} hover>
                                <TableCell>{s.phrase}</TableCell>
                                <TableCell>{s.count}</TableCell>
                            </TableRow>
                        ))}
                        {dummySearches.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2} align="center">
                                    No data in selected range
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            )}

            {/* ------------- Tab: Sessions */}
            {tab === 'sessions' && (
                <>
                    {loading && (
                        <Box display="flex" justifyContent="center" my={4}>
                            <CircularProgress size={32} />
                        </Box>
                    )}

                    {error && (
                        <Typography color="error" align="center" my={2}>
                            {error}
                        </Typography>
                    )}

                    {!loading && !error && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: '#fff' }}>
                                        User ({AUTH.providerLabel || 'Identity provider'} ID)
                                    </TableCell>
                                    <TableCell sx={{ color: '#fff' }}>Sessions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sessions.map((u) => (
                                    <React.Fragment key={u.user}>
                                        {/* Zeile pro User */}
                                        <TableRow hover>
                                            <TableCell>
                                                {/* Auf-/Zuklappen */}
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setOpenUser(openUser === u.user ? null : u.user)}
                                                    sx={{ mr: 0.5 }}
                                                >
                                                    {openUser === u.user ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                </IconButton>

                                                {/* User-ID anzeigen */}
                                                {u.user}

                                                {/* OneLogin-Link */}
                                                {AUTH.userManagementUrlTemplate && (
                                                    <IconButton
                                                        component="a"
                                                        href={AUTH.userManagementUrlTemplate.replace(
                                                            '{userId}',
                                                            encodeURIComponent(u.user)
                                                        )}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        size="small"
                                                        sx={{ ml: 0.5 }}
                                                    >
                                                        <LaunchIcon fontSize="inherit" />
                                                    </IconButton>
                                                )}
                                            </TableCell>

                                            <TableCell>{u.sessions.length}</TableCell>
                                        </TableRow>

                                        {/* Detailzeile */}
                                        <TableRow>
                                            <TableCell
                                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                                colSpan={2}
                                            >
                                                <Collapse
                                                    in={openUser === u.user}
                                                    timeout="auto"
                                                    unmountOnExit
                                                >
                                                    <Box sx={{ p: 2 }}>
                                                        {u.sessions.map((s) => (
                                                            <Box key={s.id} mb={1}>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    {new Date(s.started).toLocaleString()}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{ ml: 2 }}
                                                                >
                                                                    {s.articles.join(', ')}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                                {sessions.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={2} align="center">
                                            No sessions in selected range
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </>
            )}
            {notification}
        </Box>
    );
};

export default Analytics;

